import { createSlice } from '@reduxjs/toolkit';

import { LOG_OUT_BIDDER } from '@/redux/modules/account/logout/logout.actions';
import { LOAD_SAVED_SEARCHES_SUCCESS, type LoadSavedSearchesSuccessAction } from '@/redux/modules/actions';
import {
    DELETE_SAVE_SEARCH_FAIL,
    DELETE_SAVE_SEARCH_REQUEST,
    DELETE_SAVE_SEARCH_SUCCESS,
    type DeleteSaveSearchSuccessAction,
    deleteSavedSearch,
    fetchRecentSearches,
    removeRecentSearch,
    setNewSignupSearchParams,
    submitRecentSearch,
    submitSaveSearch,
} from '@/redux/modules/search/save/saveSearch.actions';
import { createSaveSearchKeyBySearchParams } from '@/redux/modules/search/save/saveSearch.helpers';
import { defaultSaveSearchSlice, type SaveSearchSlice } from '@/redux/modules/search/save/saveSearch.types';
import { toFacetUrlParams } from '@/utils/savedSearchFacets';

const startSubmitting = (slice: SaveSearchSlice) => {
    slice.submitted = true;
    slice.success = false;
};

const markUnfollowed = (slice: SaveSearchSlice, key: string) => {
    const followedSearch = slice.byParams[key];
    if (followedSearch) {
        followedSearch.deleted = true;
    }
    slice.error = false;
    slice.submitted = false;
    slice.success = true;
};

// byParams is left alone: any entry under the key makes getIsSavedSearch report the search as followed.
const markFailed = (slice: SaveSearchSlice) => {
    slice.error = true;
    slice.submitted = false;
    slice.success = false;
};

const saveSearchSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(fetchRecentSearches.pending, (slice) => {
            slice.recentSearchesLoading = true;
        });

        builder.addCase(fetchRecentSearches.fulfilled, (slice, { payload }) => {
            slice.recentSearches = payload ?? [];
            slice.recentSearchesLoaded = Date.now();
            slice.recentSearchesLoading = false;
        });

        builder.addCase(fetchRecentSearches.rejected, (slice) => {
            slice.recentSearchesLoading = false;
        });

        builder.addCase(submitRecentSearch.fulfilled, (slice, { payload }) => {
            // Null when savedsearch-api declined to store the search, so there is no entry to prepend
            if (payload) {
                slice.recentSearches.unshift(payload);
            }
        });

        builder.addCase(removeRecentSearch.pending, (slice, { meta }) => {
            slice.recentSearches = slice.recentSearches.filter((search) => search.hash !== meta.arg.hash);
        });

        builder.addCase(submitSaveSearch.pending, (slice) => {
            startSubmitting(slice);
        });

        builder.addCase(submitSaveSearch.fulfilled, (slice, { payload }) => {
            slice.byParams[payload.key] = {
                deleted: false,
                key: payload.key,
                savedSearchId: payload.savedSearchId,
            };
            slice.submitted = false;
            slice.success = true;
        });

        builder.addCase(submitSaveSearch.rejected, (slice) => {
            markFailed(slice);
        });

        builder.addCase(deleteSavedSearch.pending, (slice) => {
            startSubmitting(slice);
        });

        builder.addCase(deleteSavedSearch.fulfilled, (slice, { payload }) => {
            markUnfollowed(slice, payload);
        });

        builder.addCase(deleteSavedSearch.rejected, (slice) => {
            markFailed(slice);
        });

        builder.addCase(setNewSignupSearchParams.fulfilled, (slice, { payload }) => {
            slice.newSignupSearchParams = payload;
        });

        // The followed-searches list module (savedSearch.ts) unfollows through its own endpoints and
        // reports it with these legacy actions, so the Follow buttons stay in sync with My Searches.
        builder.addCase(DELETE_SAVE_SEARCH_REQUEST, (slice) => {
            startSubmitting(slice);
        });

        builder.addCase<typeof DELETE_SAVE_SEARCH_SUCCESS, DeleteSaveSearchSuccessAction>(
            DELETE_SAVE_SEARCH_SUCCESS,
            (slice, { payload }) => {
                markUnfollowed(slice, payload);
            }
        );

        builder.addCase(DELETE_SAVE_SEARCH_FAIL, (slice) => {
            markFailed(slice);
        });

        builder.addCase<typeof LOAD_SAVED_SEARCHES_SUCCESS, LoadSavedSearchesSuccessAction>(
            LOAD_SAVED_SEARCHES_SUCCESS,
            (slice, { payload }) => {
                payload?.forEach((search) => {
                    const key = createSaveSearchKeyBySearchParams(toFacetUrlParams(search.facets));
                    if (key) {
                        slice.byParams[key] = {
                            deleted: false,
                            key,
                            savedSearchId: search.savedSearchId,
                        };
                    }
                });
                slice.submitted = false;
                slice.success = true;
            }
        );

        builder.addCase(LOG_OUT_BIDDER, () => defaultSaveSearchSlice);
    },
    initialState: defaultSaveSearchSlice,
    name: 'saveSearch',
    reducers: {},
});

export const { reducer: saveSearchReducer } = saveSearchSlice;
